// Libs
import { ModelBase } from "@shoothill/core";
import * as MobX from "mobx";
import { StoresInstance } from "Globals/Stores";
import { BackgroundInformationDTO } from "Globals/Models/DTO/BackgroundInformationDTO";
import { BackgroundInformationDisabilityImpact } from "./BackgroundInformationDisabilityImpact";
import { DisabilityImpactDTO, DisabilityImpactModel } from "Shared";

export class BackgroundInformationModel extends ModelBase<BackgroundInformationModel, BackgroundInformationDTO> {
    @MobX.observable public id: string | null = null;
    @MobX.observable public formId: string = "";
    @MobX.observable public applicantId: string = "";
    @MobX.observable public isBritishOrEUCitizen: boolean = true;
    @MobX.observable public isPassportIndefinite: boolean = true;
    @MobX.observable public visaEndDate: Date | null = null;
    @MobX.observable public ageId: string = "placeholder";
    @MobX.observable public gender: string = "placeholder";
    @MobX.observable public ethnicityId: string = "placeholder";
    @MobX.observable public ethnicityOther: string = "";
    @MobX.observable public sexualOrientationId: string = "placeholder";
    @MobX.observable public religionId: string = "placeholder";
    @MobX.observable public disabilityId: string = "placeholder";
    @MobX.observable public disabilityOther: string = "";
    @MobX.observable public disabilityImpacts = MobX.observable<DisabilityImpactModel>([]);
    @MobX.observable public disabilityImpactOtherDetail: string = "";
    @MobX.observable public spokenLanguageId: string | null = null;
    @MobX.observable public spokenLanguageOther: string | null = null;
    @MobX.observable public specialRequirementsId: string = "placeholder";
    @MobX.observable public nationalityId: string = "placeholder";
    @MobX.observable public developmentalConditionId: string | null = null;
    @MobX.observable public developmentalConditionOther: string | null = null;
    @MobX.observable public supportNeedsId: string | null = null;
    @MobX.observable public supportNeedsOther: string | null = null;
    @MobX.observable public languageBarrierId: string | null = null;

    @MobX.observable public createdDate: Date | null = null;
    @MobX.observable public isDeleted: boolean = false;

    @MobX.action
    public fromDto = (dto: BackgroundInformationDTO): void => {
        if (dto !== null && dto !== undefined) {
            this.id = dto.id;
            this.formId = dto.formId;
            this.applicantId = dto.applicantId;
            this.isBritishOrEUCitizen = dto.isBritishOrEUCitizen;
            this.isPassportIndefinite = dto.isPassportIndefinite;
            this.visaEndDate = dto.visaEndDate;
            this.ageId = dto.ageId == "" ? "placeholder" : dto.ageId;
            this.gender = dto.gender == "" ? "placeholder" : dto.gender;
            this.ethnicityId = dto.ethnicityId == "" ? "placeholder" : dto.ethnicityId;
            this.ethnicityOther = dto.ethnicityOther;
            this.spokenLanguageId = dto.spokenLanguageId;
            this.spokenLanguageOther = dto.spokenLanguageOther;
            this.sexualOrientationId = dto.sexualOrientationId == "" ? "placeholder" : dto.sexualOrientationId;
            this.religionId = dto.religionId == "" ? "placeholder" : dto.religionId;
            this.disabilityId = dto.disabilityId == "" ? "placeholder" : dto.disabilityId;
            this.disabilityOther = dto.disabilityOther;

            this.disabilityImpacts.replace(
                dto.disabilityImpacts.map((di) => {
                    const impact = new DisabilityImpactModel();
                    impact.fromDto(di);
                    return impact;
                }),
            );
            const otherImpact = dto.disabilityImpacts.find((di) => (di.name ?? "").toLowerCase().includes("other"));
            this.disabilityImpactOtherDetail = otherImpact?.otherDetail ?? "";

            this.specialRequirementsId = dto.specialRequirementsId == "" ? "placeholder" : dto.specialRequirementsId;
            this.nationalityId = dto.nationalityId == "" ? "placeholder" : dto.nationalityId;
            this.developmentalConditionId = dto.developmentalConditionId;
            this.developmentalConditionOther = dto.developmentalConditionOther;
            this.supportNeedsId = dto.supportNeedsId;
            this.supportNeedsOther = dto.supportNeedsOther;
            this.languageBarrierId = dto.languageBarrierId;
            this.createdDate = dto.createdDate;
            this.isDeleted = dto.isDeleted;
        }
    };

    @MobX.action
    public toDto = (): BackgroundInformationDTO => {
        let model: BackgroundInformationDTO = {
            id: this.id,
            formId: this.formId,
            applicantId: this.applicantId,
            isBritishOrEUCitizen: this.isBritishOrEUCitizen,
            isPassportIndefinite: this.isPassportIndefinite,
            visaEndDate: this.visaEndDate,
            ageId: this.ageId == "placeholder" ? "" : this.ageId,
            gender: this.gender == "placeholder" ? "" : this.gender,
            ethnicityId: this.ethnicityId == "placeholder" ? "" : this.ethnicityId,
            ethnicityOther: this.ethnicityOther,
            sexualOrientationId: this.sexualOrientationId == "placeholder" ? "" : this.sexualOrientationId,
            religionId: this.religionId == "placeholder" ? "" : this.religionId,
            disabilityId: this.disabilityId == "placeholder" ? "" : this.disabilityId,
            disabilityOther: this.disabilityOther,
            disabilityImpacts: this.disabilityImpacts.map((di) => {
                const dto = di.toDto() as DisabilityImpactDTO;
                if ((di.name ?? "").toLowerCase().includes("other")) {
                    dto.otherDetail = this.disabilityImpactOtherDetail || undefined;
                }
                return dto;
            }),
            spokenLanguageId: this.spokenLanguageId,
            spokenLanguageOther: this.spokenLanguageOther,
            specialRequirementsId: this.specialRequirementsId == "placeholder" ? "" : this.specialRequirementsId,
            nationalityId: this.nationalityId == "placeholder" ? "" : this.nationalityId,
            developmentalConditionId: this.developmentalConditionId,
            developmentalConditionOther: this.developmentalConditionOther,
            supportNeedsId: this.supportNeedsId,
            supportNeedsOther: this.supportNeedsOther,
            languageBarrierId: this.languageBarrierId,
            createdDate: this.createdDate,
            isDeleted: this.isDeleted,
        };
        return model;
    };
}
